import clsx from 'clsx';

import { rowBackgroundColorClassMap } from '../../../constants';
import { ColumnWidths } from '../TeamsTable.constants';
import type { TableRowData } from '../TeamsTable.types';
import Jersey from '../../Jersey/Jersey';

import TeamsTableFormCell from './TeamsTableFormCell';

type TeamsTableRowProps = TableRowData & {
  /** Total number of columns in the table grid for layout purposes */
  columnCount: number;
  /** Whether this is the first row in the table (affects styling) */
  isFirstRow: boolean;
  /** Whether to display the form column showing recent match results */
  shouldDisplayFormColumn: boolean;
};

export default function TeamsTableRow({
  position,
  color,
  points,
  teamName,
  teamImageUrl,
  jerseySrc,
  playedMatches,
  wins,
  draws,
  losses,
  form,
  isHighlighted,
  columnCount,
  isFirstRow,
  shouldDisplayFormColumn,
}: TeamsTableRowProps) {
  const renderTeamImage = () => {
    if (jerseySrc) {
      return <Jersey jerseySrc={jerseySrc} size={18} />;
    }

    if (teamImageUrl) {
      return (
        <img className="w-[18px] h-[18px] p-px" src={teamImageUrl} alt={`${teamName} team logo`} />
      );
    }

    return null;
  };

  return (
    <div
      className={clsx(
        'relative',
        'grid',
        'grid-cols-subgrid',
        'items-center',
        'h-[44px]',
        '[&+&]:border-t',
        'border-pmw-separator-light',
        'dark:border-pmw-separator-dark',
        { 'bg-pmw-teams-table-highlight-bg': isHighlighted },
      )}
      style={{
        gridColumn: `span ${columnCount} / span ${columnCount}`,
      }}
    >
      <div
        className={clsx(
          'absolute',
          'left-0',
          {
            'top-0': isFirstRow,
            'h-full': isFirstRow,
            '-top-px': !isFirstRow,
            'h-[calc(100%+1px)]': !isFirstRow,
          },
          color && rowBackgroundColorClassMap[color],
        )}
        style={{
          width: ColumnWidths.COLOR,
        }}
      />
      {/* Empty div to take color column. The actual color is absolute positioned and doesn't take up the space */}
      <div />
      <span className="text-right font-robotoRegular font-normal text-[12px] leading-[16px] text-pmw-label-tertiary-light dark:text-pmw-label-tertiary-dark">
        {position}
      </span>
      <div className="flex items-center gap-1">
        {renderTeamImage()}
        <span className="line-clamp-2 wrap-break-word font-robotoMedium font-medium text-[12px] leading-[16px] text-pmw-label-primary-light dark:text-pmw-label-primary-dark">
          {teamName}
        </span>
      </div>
      <span className="text-center font-robotoRegular font-normal text-[12px] leading-[16px] text-pmw-label-secondary-light dark:text-pmw-label-secondary-dark">
        {playedMatches}
      </span>
      <span className="text-center font-robotoRegular font-normal text-[12px] leading-[16px] text-pmw-label-secondary-light dark:text-pmw-label-secondary-dark">
        {wins}
      </span>
      <span className="text-center font-robotoRegular font-normal text-[12px] leading-[16px] text-pmw-label-secondary-light dark:text-pmw-label-secondary-dark">
        {draws}
      </span>
      <span className="text-center font-robotoRegular font-normal text-[12px] leading-[16px] text-pmw-label-secondary-light dark:text-pmw-label-secondary-dark">
        {losses}
      </span>
      <div className="flex items-center justify-center h-full bg-pmw-teams-table-highlight-bg">
        <span className="font-robotoMedium font-medium text-[12px] leading-[16px] text-success110 dark:text-success090">
          {points}
        </span>
      </div>
      {shouldDisplayFormColumn && <TeamsTableFormCell form={form || []} />}
    </div>
  );
}
